import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { ContentCard } from "@/pages/HomeApp";
import { Search } from "lucide-react";

export default function Library() {
  const { taxonomy, active } = useKid();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [theme, setTheme] = useState(params.get("theme") || "");
  const [ageGroup, setAgeGroup] = useState(params.get("age_group") || (active?.age_group || ""));

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get("/contents", {
        params: {
          q: q || undefined,
          category: category || undefined,
          theme: theme || undefined,
          age_group: ageGroup || undefined,
        },
      });
      setItems(data.contents);
    };
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, category, theme, ageGroup]);

  useEffect(() => {
    const p = {};
    if (q) p.q = q; if (category) p.category = category; if (theme) p.theme = theme; if (ageGroup) p.age_group = ageGroup;
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, theme, ageGroup]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <h1 className="text-3xl font-extrabold text-[#243B53]">Biblioteca</h1>
      <p className="text-[#243B53]/70">Explore por categoria, tema ou idade.</p>

      <div className="mt-6 card-soft p-4 sm:p-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#243B53]/40" size={18} />
          <input
            data-testid="library-search-input"
            placeholder='Ex: "atividade sobre amizade", "história de Davi", "versículo sobre amor"'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-full border border-[#243B53]/15 bg-[#FFF9ED] pl-11 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5DADE2]"
          />
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Selector label="Categoria" value={category} onChange={setCategory} options={[{value:"", label:"Todas"}, ...taxonomy.categories.map(c => ({value:c.key, label:`${c.emoji} ${c.label}`}))]} testid="library-category-select" />
          <Selector label="Faixa etária" value={ageGroup} onChange={setAgeGroup} options={[{value:"", label:"Todas"}, ...taxonomy.age_groups.map(g => ({value:g.key, label:g.label}))]} testid="library-age-select" />
          <Selector label="Tema" value={theme} onChange={setTheme} options={[{value:"", label:"Todos"}, ...taxonomy.themes.map(t => ({value:t.label, label:t.label}))]} testid="library-theme-select" />
          <button
            data-testid="library-clear-filters-btn"
            onClick={() => { setQ(""); setCategory(""); setTheme(""); setAgeGroup(""); }}
            className="btn-outline self-end"
          >Limpar</button>
        </div>
      </div>

      <div className="mt-6 text-sm text-[#243B53]/60">{items.length} conteúdo(s) encontrado(s)</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((c) => <ContentCard key={c.id} content={c} />)}
      </div>
      {items.length === 0 && (
        <div className="mt-10 text-center text-[#243B53]/60 card-soft py-16">
          <div className="text-5xl">🔎</div>
          <div className="mt-3 font-bold">Nada encontrado. Tente outra busca.</div>
        </div>
      )}
    </div>
  );
}

function Selector({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-[#243B53]/70">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-[#243B53]/15 bg-white px-3 py-2.5 font-semibold text-[#243B53]">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
